import React, { useEffect, useRef, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { Text, ScrollView, StatusBar } from "react-native";
import i18n from "@microdoing/shared/i18n";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { clearTeamNotifications } from "@microdoing/shared/services/notification/actions";
import {
  getTeamRequest,
  getTeamRankRequest,
} from "@microdoing/shared/services/userCampaign/actions";
import { createConversation } from "@microdoing/shared/services/team/actions";
import MyTeam from "./MyTeam";
import TeamSettingsModal from "./TeamSettingsModal";
import NewTeamModal from "app/scenes/HomeScreen/NewTeamModal";
import AnimatedBox from "app/components/common/AnimatedBox";
import { MainText, MainTitle, PressableOpacity } from "app/components/common";
import TeamRankingBox from "./TeamRankingBox";
import JouvieExplanation from "../../../components/common/JouvieExplanation";
import Theme from "@microdoing/shared/constants/Theme";
import {
  getTeam,
  getCampaignSelected,
  getIsRankingDisabled,
  getTeamRequesting,
  getTeamName,
  getUserCampaignTeamRank,
  getPreviousUserCampaignTeamRank,
} from "@microdoing/shared/services/userCampaign/selectors";
import TeamChat from "./TeamChat";
import theme from "app/Theme/Theme";
import { pluralize } from "app/utils/plurialize";
import { Box } from "app/components/common";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsTeamScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import {
  getRoute,
  getScrollTop,
} from "@microdoing/shared/services/nav/selectors";
import { getTeamNotifications } from "@microdoing/shared/services/notification/selectors";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import HeaderTabBar from "app/navigation/TabBar/HeaderTabBar";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import MessageBox from "app/components/common/Box/IconBox/MessageBox";
import normalize from "app/utils/normalize";
import {
  getLatestConversationsRequest,
  openConversationRequest,
} from "@microdoing/shared/services/message/actions";
import { getConversations } from "@microdoing/shared/services/message/selectors";
import PencilIcon from "app/components/Icons/PencilIcon";
import CircleInfoBox from "./CircleInfoBox";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  IncrementalGroup,
  Incremental,
} from "@th3rdwave/react-native-incremental";

const ROUTE_NAME = "TeamScreen";

const HIT_SLOP = { top: 20, bottom: 20, left: 20, right: 20 };

const TeamScreen = () => {
  const [
    isFirstConnectionModalVisible,
    setIsFirstConnectionModalVisible,
  ] = useState<boolean>(false);
  const [isNewTeamModalVisible, setIsNewTeamModalVisible] = useState<boolean>(
    false,
  );
  const [isTeamModalVisible, setIsTeamModalVisible] = useState<boolean>(false);
  const [firstConnectionModalShown, setFirstConnectionModalShown] = useState<
    boolean
  >(false);
  const [isJouvieVisible, setIsJouvieVisible] = useState<boolean>(false);
  const [width, setWidth] = useState(300);

  const userId = useSelector(getUserId);
  const route = useSelector(getRoute);
  const teamNotifications = useSelector(getTeamNotifications);
  const team = useSelector(getTeam);
  const teamName = useSelector(getTeamName);
  const teamRequesting = useSelector(getTeamRequesting);
  const campaignSelected = useSelector(getCampaignSelected);
  const isRankingDisabled = useSelector(getIsRankingDisabled);
  const shouldScrollTop = useSelector(getScrollTop);
  const scrollViewRef = useRef(null);
  const conversations = useSelector(getConversations);

  const teamRank = useSelector(getUserCampaignTeamRank);
  const previousTeamRank = useSelector(getPreviousUserCampaignTeamRank);

  const { steps, campaignId, teamSize, allStepsViews } = campaignSelected;
  const isTeamVisible = teamSize > 1 && team && team.users.length > 0;
  const { weekPoints = 0, points = 0 } = team;

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const teamConversationId = team.conversationId || "oldTeam";
  const [conversationNotSeen, setConversationNotSeen] = useState(0);

  const insets = useSafeAreaInsets();

  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused) {
      batch(() => {
        dispatch(getLatestConversationsRequest({ userId }));
        dispatch(
          getTeamRequest({
            campaignId: campaignSelected.campaignId,
            userId: userId,
          }),
        );
        dispatch(
          getTeamRankRequest({
            userId: userId,
            campaignId: campaignId,
          }),
        );
      });
    }
  }, [isFocused]);

  useEffect(() => {
    const convTeamSeen = conversations.filter(
      el =>
        el._id === team.conversationId &&
        !el.latestMessage.seen &&
        el.latestMessage.user._id !== userId &&
        !el.latestMessage.seenBy[userId],
    );

    setConversationNotSeen(convTeamSeen.length);
  }, [team.conversationId, conversations, userId]);

  const openChatScreen = (users, conversationIdGroup) => () => {
    const receivers = users.map(el => {
      return { _id: el.user, name: el.name, picture: el.picture };
    });

    dispatch(
      openConversationRequest({
        receivers,
        conversationId: conversationIdGroup || "",
        name: conversationIdGroup ? team.name : "",
      }),
    );
  };

  useEffect(() => {
    scrollTop();
    dispatch(changeNavField({ scrollTop: false }));
  }, [shouldScrollTop]);

  const scrollTop = () => {
    if (scrollViewRef && scrollViewRef.current) {
      scrollViewRef?.current?.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  useEffect(() => {
    if (!!team._id && !team.conversationId) {
      dispatch(
        createConversation({
          userId: userId,
          teamId: team._id,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (
      isNewTeamEvent(teamNotifications) &&
      route === ROUTE_NAME &&
      !isNewTeamModalVisible
    ) {
      setIsNewTeamModalVisible(true);
      dispatch(clearTeamNotifications({}));
    }

    if (
      isTeamVisible &&
      !teamRequesting &&
      // prevProps.teamRequesting && // ??? Est ce bien necessaire?
      !steps[0].done &&
      allStepsViews.length < 1 &&
      !firstConnectionModalShown &&
      route === ROUTE_NAME &&
      !isFirstConnectionModalVisible
    ) {
      setIsFirstConnectionModalVisible(true);
      setFirstConnectionModalShown(true);
      dispatch(clearTeamNotifications({}));
    }
  }, [teamRequesting]);

  const findDimensions = ({ height, width }) => {
    setWidth(width);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  const isNewTeamEvent = notifications => {
    return (
      notifications.findIndex(
        el => el.eventType === "newTeam" && el.campaignId === campaignId,
      ) > -1
    );
  };

  const triggerTeamModal = () => {
    setIsTeamModalVisible(prevState => !prevState);
  };

  const displayNewTeamText = () => {
    if (isTeamVisible) {
      return [
        <Text
          style={{ textAlign: "center", color: theme.colors["neutral.900"] }}
        >
          {i18n.t("first-connection-popup-text-third-1")}
          <Text style={{ fontWeight: "600", color: theme.colors["brand.500"] }}>
            {i18n.t("first-connection-popup-text-third-2")}
          </Text>
        </Text>,
      ];
    }
    return [];
  };

  const displayFirstConnectionText = () => {
    if (isTeamVisible) {
      return [
        <Text
          style={{ textAlign: "center", color: theme.colors["neutral.900"] }}
        >
          {i18n.t("first-connection-popup-text-third-1")}
          <Text style={{ fontWeight: "600" }}>
            {i18n.t("first-connection-popup-text-third-2")}
          </Text>
        </Text>,
      ];
    }
    return [];
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsTeamScreen}>
      <Box
        style={{
          paddingHorizontal: isWebDesktop ? "null" : theme.spacing.sm,
          flex: 1,
          paddingTop: insets.top,
        }}
      >
        <StatusBar animated backgroundColor={theme.colors["neutral.75"]} />
        <NewTeamModal
          isVisible={isFirstConnectionModalVisible || isNewTeamModalVisible}
          userId={userId}
          team={team}
          triggerModal={() => {
            setIsFirstConnectionModalVisible(false);
            setIsNewTeamModalVisible(false);
          }}
          title={
            isFirstConnectionModalVisible
              ? i18n.t("first-connection-popup-title")
              : i18n.t("new-team-popup-title")
          }
          multipleText={
            isFirstConnectionModalVisible
              ? displayFirstConnectionText()
              : displayNewTeamText()
          }
        />
        <TeamSettingsModal
          team={team}
          isVisible={isTeamModalVisible}
          triggerTeamModal={triggerTeamModal}
        />

        {!isWebDesktop && (
          <HeaderTabBar
            title={
              <MainTitle variant="l8-bold">
                {i18n.t("breadcrumb-team")}
              </MainTitle>
            }
          />
        )}
        <ScrollView
          style={{ width: "100%" }}
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollViewContainer}
        >
          <Box
            flexDirection={isWebDesktop ? "row" : "column"}
            style={{ columnGap: isWebDesktop ? theme.spacing.xm : 0 }}
            onLayout={handleOnLayout}
          >
            <Box
              width={isWebDesktop ? width / 2 - theme.spacing.xm / 2 : "100%"}
            >
              <Box
                paddingHorizontal="m"
                paddingVertical="sm"
                paddingBottom="m"
                borderRadius={8}
                backgroundColor="neutral.white"
              >
                <Box
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                  width="100%"
                >
                  <Box flexDirection="column" maxWidth="70%">
                    <PressableOpacity
                      hitSlop={HIT_SLOP}
                      onPress={triggerTeamModal}
                      style={{ flexDirection: "row" }}
                    >
                      <MainTitle
                        variant="l6-bold"
                        numberOfLines={1}
                        marginRight="xxs"
                      >
                        {teamName}
                      </MainTitle>
                      <PencilIcon color={theme.colors["brand.500"]} />
                    </PressableOpacity>
                    <MainText
                      marginTop="xxs"
                      variant="l3-medium"
                      color="neutral.600"
                    >
                      {team?.users?.length}{" "}
                      {pluralize("member", team?.users?.length)}
                    </MainText>
                  </Box>
                  <PressableOpacity
                    onPress={openChatScreen(team.users, teamConversationId)}
                  >
                    <MessageBox
                      backgroundColor="brand.500"
                      iconColor={theme.colors["neutral.white"]}
                      borderRadius={normalize(20)}
                    />
                    {conversationNotSeen !== 0 && (
                      <Box
                        height={normalize(15)}
                        width={normalize(15)}
                        borderRadius={normalize(10)}
                        borderWidth={1.5}
                        borderColor="neutral.white"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="system.red.900"
                        position="absolute"
                        top={normalize(-2)}
                        right={normalize(-5)}
                        zIndex={3}
                      />
                    )}
                  </PressableOpacity>
                </Box>

                {!!isTeamVisible && <MyTeam navigation={navigation} />}
              </Box>

              <IncrementalGroup>
                <Incremental>
                  <Box
                    flexDirection="row"
                    marginTop="sm"
                    justifyContent="space-between"
                  >
                    <Box width="31%">
                      <CircleInfoBox
                        isUp={teamRank <= previousTeamRank}
                        text={`${teamRank}e`}
                        legend={i18n.t("ranking")}
                      />
                    </Box>
                    <Box width="31%">
                      <CircleInfoBox
                        isUp
                        text={`${points}`}
                        legend={i18n.t("total-points")}
                      />
                    </Box>

                    <Box width="31%">
                      <CircleInfoBox
                        isUp
                        text={`${weekPoints}`}
                        legend={i18n.t("weekly-points")}
                      />
                    </Box>
                  </Box>
                </Incremental>
              </IncrementalGroup>
            </Box>
            <IncrementalGroup>
              <Incremental>
                <Box
                  width={
                    isWebDesktop ? width / 2 - theme.spacing.xm / 2 : "100%"
                  }
                >
                  {!isRankingDisabled && <TeamRankingBox />}
                </Box>
              </Incremental>
            </IncrementalGroup>
          </Box>
        </ScrollView>
      </Box>
    </WebDesktopView>
  );
};

const styles = {
  scrollViewContainer: {
    width: "100%",
    paddingBottom: isWebDesktop ? 0 : theme.spacing.m,
    marginBottom: theme.spacing.m,
  },
  box: {
    marginTop: 15,
    paddingHorizontal: 0,
  },
};

export default TeamScreen;
